/**
 * @fileoverview Nest generated Day One attachments under their parent pack.
 *
 * Generated-precedent attachments (ROCAP parts, director-pack notices, …)
 * are stored as their own `jobPrecedents` rows so they can be filled,
 * previewed, and downloaded. The Day One table used to list those rows
 * as equals of the cover letter, which made "send this pack" look like
 * six separate emails. This module walks the live `attachments` links
 * and rebuilds the table as parent packs with nested attachment rows.
 */

import type { Id } from "@cvx/_generated/dataModel";
import {
  projectDayOneRow,
  type DayOneListAttachment,
  type DayOneListItem,
  type DayOnePrecedentRow,
} from "./types";

const ROW_ID_SEP = "::";
const FILE_ROW_PREFIX = "file:";

/**
 * One nested slot under a pack, in template attach order: either a
 * sibling generated document or an uploaded / job-document file.
 */
type PackChild =
  | { kind: "generated"; childId: Id<"jobPrecedents"> }
  | { kind: "file"; attachment: DayOneListAttachment; slotIndex: number };

/**
 * True when an attachment slot points at another generated Day One
 * document rather than an uploaded file / job document.
 */
function isGeneratedAttachment(
  attachment: NonNullable<DayOneListItem["attachments"]>[number],
): boolean {
  return (
    attachment.sourceKind === "generated_precedent" ||
    Boolean(attachment.generatedPrecedentId) ||
    Boolean(attachment.generatedTemplateKey)
  );
}

/**
 * Resolve the sibling `jobPrecedents` row a generated-precedent slot
 * points at. Prefer the explicit id; fall back to template-key + same
 * contact (the same rule `listByJob` uses when the id was never
 * back-filled).
 */
function resolveGeneratedChildId(
  parent: DayOneListItem,
  attachment: NonNullable<DayOneListItem["attachments"]>[number],
  byId: Map<string, DayOneListItem>,
  byTemplateKey: Map<string, DayOneListItem[]>,
): Id<"jobPrecedents"> | null {
  if (attachment.generatedPrecedentId) {
    return byId.has(attachment.generatedPrecedentId)
      ? attachment.generatedPrecedentId
      : null;
  }
  if (!attachment.generatedTemplateKey) return null;
  const candidates = byTemplateKey.get(attachment.generatedTemplateKey) ?? [];
  const sameContact =
    candidates.find(
      (candidate) =>
        (parent.jobContactId &&
          candidate.jobContactId === parent.jobContactId) ||
        (parent.firmContactId &&
          candidate.firmContactId === parent.firmContactId),
    ) ?? candidates[0];
  return sameContact?._id ?? null;
}

function countVisibleAttachments(doc: DayOneListItem): number {
  return (doc.attachments ?? []).filter((att) => att.status !== "skipped")
    .length;
}

/**
 * Build the nested row for an uploaded / job-document attachment. It has
 * no `jobPrecedents` row of its own, so it borrows the parent's `_id`
 * (never selectable, never counted as an attachment-only precedent) and
 * gets a unique `tableRowId` from its slot index.
 */
function buildFileRow(
  parent: DayOneListItem,
  parentRow: DayOnePrecedentRow,
  attachment: DayOneListAttachment,
  tableRowId: string,
): DayOnePrecedentRow {
  const slotName = attachment.name ?? "Attachment";
  return {
    _id: parent._id,
    tableRowId,
    templateKey: parent.templateKey,
    name: attachment.fileName ?? slotName,
    category: parent.category,
    sendStatus: parent.sendStatus,
    isAttachment: true,
    attachedToName: parent.name,
    parentPrecedentId: parent._id,
    attachmentCount: 0,
    fileAttachment: {
      slotName,
      fileName: attachment.fileName,
      fileType: attachment.fileType,
      downloadUrl: attachment.downloadUrl ?? null,
      status: attachment.status ?? "missing",
    },
    // Inherit the contact so grouping by contact keeps the file with its pack.
    jobContactId: parentRow.jobContactId,
    contactRoleKind: parentRow.contactRoleKind,
    firmContactId: parentRow.firmContactId,
    firmContactCategory: parentRow.firmContactCategory,
    contactDisplayName: parentRow.contactDisplayName,
    contactExists: parentRow.contactExists,
  };
}

function buildRow(
  doc: DayOneListItem,
  opts: {
    isAttachment: boolean;
    tableRowId: string;
    attachedToName?: string;
    parentPrecedentId?: Id<"jobPrecedents">;
    childrenByParent: Map<string, PackChild[]>;
    byId: Map<string, DayOneListItem>;
    visited: Set<string>;
  },
): DayOnePrecedentRow {
  const visited = new Set(opts.visited);
  visited.add(doc._id);

  const self: DayOnePrecedentRow = {
    ...projectDayOneRow(doc),
    tableRowId: opts.tableRowId,
    isAttachment: opts.isAttachment,
    attachedToName: opts.attachedToName,
    parentPrecedentId: opts.parentPrecedentId,
    attachmentCount: countVisibleAttachments(doc),
  };

  const subRows: DayOnePrecedentRow[] = [];
  for (const entry of opts.childrenByParent.get(doc._id) ?? []) {
    if (entry.kind === "file") {
      subRows.push(
        buildFileRow(
          doc,
          self,
          entry.attachment,
          `${opts.tableRowId}${ROW_ID_SEP}${FILE_ROW_PREFIX}${entry.slotIndex}`,
        ),
      );
      continue;
    }
    if (visited.has(entry.childId)) continue;
    const child = opts.byId.get(entry.childId);
    if (!child) continue;
    subRows.push(
      buildRow(child, {
        isAttachment: true,
        tableRowId: `${opts.tableRowId}${ROW_ID_SEP}${child._id}`,
        attachedToName: doc.name,
        parentPrecedentId: doc._id,
        childrenByParent: opts.childrenByParent,
        byId: opts.byId,
        visited,
      }),
    );
  }

  return {
    ...self,
    subRows: subRows.length > 0 ? subRows : undefined,
  };
}

/**
 * Rebuild a flat Day One list so every attachment nests under the pack
 * that attaches it: generated-precedent siblings become nested document
 * rows, and uploaded / job-document files become nested file rows
 * (MOR-971). Rows that are only ever referenced as an attachment
 * disappear from the top level; independently generated documents (no
 * parent points at them) stay as standalone packs.
 *
 * A document referenced by several packs is nested under each of them
 * (unique `tableRowId` per appearance) so each pack still shows its
 * full attachment list.
 */
export function nestGeneratedAttachments(
  docs: DayOneListItem[],
): DayOnePrecedentRow[] {
  const byId = new Map<string, DayOneListItem>();
  const byTemplateKey = new Map<string, DayOneListItem[]>();
  for (const doc of docs) {
    byId.set(doc._id, doc);
    const list = byTemplateKey.get(doc.templateKey) ?? [];
    list.push(doc);
    byTemplateKey.set(doc.templateKey, list);
  }

  const childrenByParent = new Map<string, PackChild[]>();
  const referencedIds = new Set<string>();

  for (const doc of docs) {
    const children: PackChild[] = [];
    const seen = new Set<string>();
    (doc.attachments ?? []).forEach((attachment, slotIndex) => {
      if (!isGeneratedAttachment(attachment)) {
        // Skipped file slots have nothing to show; skipped generated
        // siblings still nest (the pack points at them) so they don't
        // surface as stray top-level documents.
        if (attachment.status !== "skipped") {
          children.push({ kind: "file", attachment, slotIndex });
        }
        return;
      }
      const childId = resolveGeneratedChildId(
        doc,
        attachment,
        byId,
        byTemplateKey,
      );
      if (!childId || childId === doc._id || seen.has(childId)) return;
      seen.add(childId);
      children.push({ kind: "generated", childId });
      referencedIds.add(childId);
    });
    if (children.length > 0) childrenByParent.set(doc._id, children);
  }

  const topLevel = docs.filter((doc) => !referencedIds.has(doc._id));
  return topLevel.map((doc) =>
    buildRow(doc, {
      isAttachment: false,
      tableRowId: doc._id,
      childrenByParent,
      byId,
      visited: new Set(),
    }),
  );
}

/** Precedent ids that only appear nested under a pack (never as a top-level sendable document). */
export function collectAttachmentOnlyIds(
  rows: DayOnePrecedentRow[],
): Set<string> {
  const ids = new Set<string>();
  const walk = (row: DayOnePrecedentRow) => {
    for (const child of row.subRows ?? []) {
      // File rows borrow their pack's id — they aren't precedents.
      if (child.fileAttachment) continue;
      ids.add(child._id);
      walk(child);
    }
  };
  for (const row of rows) walk(row);
  return ids;
}

/** Walk parent + nested rows (depth-first) for selection / search. */
export function flattenDayOneRows(
  rows: DayOnePrecedentRow[],
): DayOnePrecedentRow[] {
  const out: DayOnePrecedentRow[] = [];
  const walk = (row: DayOnePrecedentRow) => {
    out.push(row);
    for (const child of row.subRows ?? []) walk(child);
  };
  for (const row of rows) walk(row);
  return out;
}

export function rowMatchesSearch(
  row: DayOnePrecedentRow,
  query: string,
): boolean {
  const v = query.toLowerCase();
  if (row.name.toLowerCase().includes(v)) return true;
  if (row.contactDisplayName?.toLowerCase().includes(v)) return true;
  if (row.attachedToName?.toLowerCase().includes(v)) return true;
  if (row.fileAttachment?.slotName.toLowerCase().includes(v)) return true;
  return false;
}

export function rowOrDescendantMatchesSearch(
  row: DayOnePrecedentRow,
  query: string,
): boolean {
  if (rowMatchesSearch(row, query)) return true;
  return (row.subRows ?? []).some((child) =>
    rowOrDescendantMatchesSearch(child, query),
  );
}
